import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { RowSelectionState } from '@tanstack/react-table'
import { useDataTableContext } from 'components/DataTable'
import { CompanyTypeSlug, TableTypeSlug } from 'models/companies'
import { DealCreate } from 'models/deals'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import apiService from 'services/api'
import { z } from 'zod'
import {
  DEAL_TABLE_KEY,
  getCampaignTableKey,
} from '../../../constants/tableQueryKeys'
import { Modal } from '../../UI/Modal/Modal'
import confirm from 'components/dialogConfirm'
import DealDetailsStep from './DealDetailsStep'
import {
  checkDoorSelectionRules,
  selectDoorsStepFormSchema,
  SelectDoorsStepWithControl,
} from './SelectDoorsStep'

const dealsFormSchema = z
  .object({
    deals: z.array(
      z.object({
        create: z.boolean(),
        product: z.number(),
        product_variant: z.number().optional().nullable(),
        monthly_volume_override: z.number().min(0).optional().nullable(),
        monthly_revenue_override: z.number().min(0).optional().nullable(),
        close_date_override: z.string().optional().nullable(),
        account_owner: z.number().optional().nullable(),
        sales_stage: z.number().optional().nullable(),
        origin_campaign_id: z.number().optional().nullable(),
        name: z
          .union([
            z.string().max(100, 'Deal name must be 100 characters or less'),
            z.null(),
            z.undefined(),
          ])
          .optional()
          .nullable(),
      })
    ),
  })
  .merge(selectDoorsStepFormSchema)
  .superRefine((v, ctx) => {
    const dealsToCreate = v.deals.filter((deal) => deal.create)

    if (dealsToCreate.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one deal must be selected',
        path: ['custom'],
      })
    }

    v.deals.forEach((deal) => {
      if (!deal.create) return

      if (!deal.monthly_revenue_override && !deal.monthly_volume_override) {
        return
      }

      if (!deal.monthly_volume_override || !deal.monthly_revenue_override) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'If any of the override fields are set, all must be set',
          path: ['custom'],
        })
      }
    })

    checkDoorSelectionRules(v, ctx)
  })

export type DealsFormValues = z.infer<typeof dealsFormSchema>

export interface CreateDealModalForTableProps {
  data?: any[]
  idAccessor?: string
  doorIdAccessor?: string
  targetCompanyId?: undefined
  handleClose: () => void
  onDealCreated?: () => void
  show: boolean
  submitButtonText?: string
  cancelButtonText?: string
  // These relate to optimistic UI updates
  queryKey?: string[]
  optimisticUiTarget:
    | 'companies'
    | 'chains'
    | 'create-company'
    | 'deals-section'
    | 'k12'
    | 'none'
  filterAndSortParams?: Record<string, any>
  originCampaignId?: number
  companyTypeSlug: CompanyTypeSlug
  tableTypeSlug: TableTypeSlug
  forDoorSelection?: boolean
}

export interface CreateDealModalProps
  extends Omit<CreateDealModalForTableProps, 'data' | 'targetCompanyId'> {
  targetCompanyId: number | undefined
  targetDoorProxyId?: number // This is the door proxy ID, not the firstbite_id
  totalSelectedRows: number
  existingDealsByProduct?: Record<number, number>
  selectedRows?: any[]
  unselectedRows?: any[]
  rowSelection?: RowSelectionState
  isUserCreated?: boolean
  isAllRowsSelected?: boolean
}

export function CreateDealModalForTable(props: CreateDealModalForTableProps) {
  const {
    state: { rowSelection, isAllRowsSelected, totalSelectedRows },
  } = useDataTableContext()
  const unselectedRows = props?.data?.filter((_, i) => !rowSelection[i])
  const selectedRows = props?.data?.filter((_, i) => rowSelection[i])

  // {product_id: amount_of_deals}
  const existingDealsByProduct = useMemo(() => {
    const out: Record<number, number> = {}
    selectedRows?.forEach((i) =>
      i?.deals?.forEach((j: any) => {
        out[j.product] = (out?.[j.product] ?? 0) + 1
      })
    )
    return out
  }, [selectedRows])

  return (
    <CreateDealModal
      {...props}
      targetCompanyId={undefined}
      selectedRows={selectedRows}
      unselectedRows={unselectedRows}
      rowSelection={rowSelection}
      existingDealsByProduct={existingDealsByProduct}
      totalSelectedRows={totalSelectedRows}
      isAllRowsSelected={isAllRowsSelected}
    />
  )
}

export default function CreateDealModal(props: CreateDealModalProps) {
  const [currentStep, setCurrentStep] = useState(1)
  const queryClient = useQueryClient()
  const api = apiService()

  const chainIdAccessor = useMemo(() => {
    return props.idAccessor
  }, [props.idAccessor])

  const doorIdAccessor = useMemo(() => {
    return props.doorIdAccessor ?? 'id'
  }, [props.doorIdAccessor])

  const idAccessorToUse = useMemo(() => {
    return props.forDoorSelection ? doorIdAccessor : chainIdAccessor
  }, [props.forDoorSelection, doorIdAccessor, chainIdAccessor])

  const targetCompanyId = useMemo(() => {
    if (props.targetCompanyId) {
      return props.targetCompanyId
    }
    if (props.selectedRows?.length === 1) {
      return chainIdAccessor
        ?.split('.')
        .reduce((acc, key) => acc[key], props.selectedRows[0])
    }
    return undefined
  }, [props.targetCompanyId, props.selectedRows, chainIdAccessor])

  const { data: predictions, isLoading: isLoading } =
    api.useGetProductPredictions({
      chainProxyId: targetCompanyId,
    })

  // Get company details for the placeholder
  const { data: companyDetails } = api.useGetCompany(targetCompanyId || 0)

  const shouldRequestForDoorSelection = !!targetCompanyId
  const totalSteps = useMemo(() => {
    // If the modal is for door selection, we always have 1 step
    // No need to select doors since we are already in the door selection context
    if (props.forDoorSelection) {
      return 1
    }
    return shouldRequestForDoorSelection ? 2 : 1
  }, [shouldRequestForDoorSelection, props.forDoorSelection])

  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isValid },
    reset,
    trigger,
    setValue,
  } = useForm({
    resolver: zodResolver(dealsFormSchema),
    defaultValues: { deals: [], apply_to: 'entire_company' } as DealsFormValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
  })

  useEffect(() => {
    if (!predictions) return
    const deals = predictions.map((prediction) => {
      return {
        create: false,
        product: prediction.product.id,
        product_variant: null,
        // TODO determine why prediction endpoint returning incorrect deal data
        monthly_volume_override: null,
        monthly_revenue_override: null,
        close_date_override: null,
        origin_campaign_id: props.originCampaignId,
        name: null,
      }
    })
    reset({ deals, apply_to: 'entire_company' })
  }, [predictions, targetCompanyId])

  const mutation = useMutation({
    mutationFn: async ({
      deals,
      excludeIds,
      selectedIds,
      applyTo,
      doorIds,
    }: {
      deals: Partial<DealCreate>[]
      applyTo?: string
      doorIds?: number[]
      excludeIds: string[]
      selectedIds: string[]
      affectedRowsIndexes: number[]
    }) => {
      let body: any = {}

      if (props.targetCompanyId && !props.forDoorSelection) {
        body['include_ids'] = [props.targetCompanyId]
      } else if (
        props.forDoorSelection &&
        props.targetDoorProxyId &&
        props.targetCompanyId
      ) {
        // For door pages, include_ids should contain the door proxy ID
        // All door endpoints (restaurants, lodging, convenience, unmatched) use CreateDoorDealsViewSetMixin
        // which expects door_proxy IDs in include_ids
        body['include_ids'] = [props.targetDoorProxyId]
        body['apply_to'] = 'select_locations'
      } else {
        body['include_ids'] = selectedIds
        body['exclude_ids'] = excludeIds

        // If all rows are selected, we don't need to send the chain_ids or contact_company_ids
        if (props.isAllRowsSelected) {
          body['include_ids'] = []
        }
      }

      body = {
        deals,
        filter_params: props.filterAndSortParams ?? {},
        include_ids: body.include_ids,
        exclude_ids: body.exclude_ids,
        ...(body.apply_to && { apply_to: body.apply_to }),
      }

      // If the modal is not for door selection, we need to apply the deals to the selected doors
      if (!props.forDoorSelection) {
        body['apply_to'] = applyTo
        body['door_ids'] = doorIds
      }

      return await api.commonCreateDeals(props.tableTypeSlug, {
        ...body,
      })
    },
    onError: (err, newTodo, context) => {
      if (!context) return
      // queryClient.setQueryData(props.queryKey ?? [], context.prev)
      toast.error('Error creating deals')
      // Always close the modal on error
      handleClose()
    },
    onSuccess: (data) => {
      if (data.status === 202) {
        toast.info(
          data.data?.message ??
            'We are processing your request, you will be notified when it is done'
        )
      } else {
        toast.success('Deals created successfully')
        // Only invalidate queries for synchronous operations
        props.onDealCreated?.()
        queryClient.invalidateQueries({ queryKey: props.queryKey })
        queryClient.invalidateQueries({ queryKey: [DEAL_TABLE_KEY] })
        if (props.originCampaignId) {
          queryClient.invalidateQueries({
            queryKey: [
              getCampaignTableKey(DEAL_TABLE_KEY, props.originCampaignId),
            ],
          })
        }
      }
      // Always close the modal on success
      handleClose()
    },
  })

  const handleClose = () => {
    props.handleClose()
    reset()
    setCurrentStep(1)
  }

  const onAccept = async () => {
    const valid = await trigger()
    if (!valid) return

    if (currentStep === totalSteps) {
      return onSubmit()
    } else {
      setCurrentStep(currentStep + 1)
    }
  }
  const onBack = useCallback(() => {
    if (currentStep === 1) {
      handleClose()
    } else {
      setCurrentStep(1)
    }
  }, [currentStep, setCurrentStep])

  const onSubmit = handleSubmit(
    async (values) => {
      const dealsToCreate: Partial<DealCreate>[] = values.deals
        .filter((deal) => deal.create)
        .map((deal) => {
          return {
            product_id: deal.product,
            product_variant_id: deal.product_variant ?? undefined,
            monthly_volume_override: deal.monthly_volume_override ?? undefined,
            monthly_revenue_override:
              deal.monthly_revenue_override ?? undefined,
            close_date_override: deal.close_date_override || undefined,
            sales_stage_id: deal.sales_stage ?? undefined,
            account_owner_id: deal.account_owner ?? undefined,
            origin_campaign_id: deal.origin_campaign_id ?? undefined,
            name: deal.name || undefined,
          }
        })

      const excludeIds =
        props.unselectedRows?.map((r) => {
          return idAccessorToUse?.split('.').reduce((acc, key) => acc[key], r)
        }) ?? []

      const selectedIds =
        props.selectedRows?.map((r) => {
          return idAccessorToUse?.split('.').reduce((acc, key) => acc[key], r)
        }) ?? []

      const affectedRowsIndexes = props.rowSelection
        ? Object.keys(props.rowSelection).map((key) => parseInt(key))
        : []

      if (props.totalSelectedRows > 1000) {
        const confirmed = await confirm(
          `You are about to create ${props.totalSelectedRows} deals. This is a large number and may take a while to complete. Are you sure you want to proceed?`,
          'Create Deals in Bulk'
        )
        if (!confirmed) return
      }

      await mutation.mutateAsync({
        deals: dealsToCreate,
        applyTo: values.apply_to,
        doorIds: values.door_ids,
        excludeIds,
        selectedIds,
        affectedRowsIndexes,
      })
    },
    (err) => console.warn(err)
  )

  const initialDoorIdsForDealModal = useMemo(() => {
    if (props.targetDoorProxyId) {
      return [props.targetDoorProxyId]
    }
    return undefined
  }, [props.targetDoorProxyId])

  // Show deal name field only when one row is selected (for opportunities table)
  // or always show it for company/door pages
  const showDealName = useMemo(() => {
    // If we have a targetCompanyId or targetDoorProxyId, this is a company/door page
    if (props.targetCompanyId || props.targetDoorProxyId) {
      return true
    }
    // For opportunities table, only show when exactly one row is selected
    return props.totalSelectedRows === 1
  }, [props.targetCompanyId, props.targetDoorProxyId, props.totalSelectedRows])

  return (
    <Modal
      open={props.show}
      onOpenChange={(open) => !open && handleClose()}
      size="xl"
      blockAccept={isLoading}
      blockCancel={isLoading}
      onAccept={onAccept}
      onCancel={onBack}
      preventCloseOnCancel
      loading={isSubmitting}
      cancelButtonProps={{
        type: 'button', // By default, it is type="submit"
      }}
      title="Add Deals"
      acceptButtonText={
        currentStep === totalSteps
          ? (props.submitButtonText ?? 'Create Deals')
          : 'Next'
      }
      acceptButtonProps={{
        disabled: !isValid,
      }}
      cancelButtonText={
        currentStep === totalSteps && totalSteps > 1
          ? 'Back'
          : (props.cancelButtonText ?? 'Cancel')
      }
      description={
        <span className="block max-w-[800px]">
          Deals allow you to track specific revenue opportunities throughout
          your sales pipeline. Each deal is done on a per product basis. For
          deals with unmatched companies, you can provide your own volume,
          revenue, and close date estimates.
        </span>
      }
    >
      {isLoading ? (
        <div className="d-flex justify-content-center overflow-hidden">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          {currentStep === 1 && (
            <DealDetailsStep
              predictions={predictions}
              control={control}
              setValue={setValue}
              companyTypeSlug={props.companyTypeSlug}
              existingDealsByProduct={props.existingDealsByProduct}
              originCampaignId={props.originCampaignId}
              showDealName={showDealName}
              companyDetails={companyDetails}
            />
          )}
          {currentStep === 2 && (
            <SelectDoorsStepWithControl
              control={control}
              chainProxyId={targetCompanyId}
              formTrigger={trigger}
              initialDoorIds={initialDoorIdsForDealModal}
            />
          )}
        </>
      )}
    </Modal>
  )
}
